import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const PILLAR_THREE_OVERVIEW = String.raw`
Pillar 3 is **Portfolio Construction Quality**. It asks whether the user built
and adjusted a disciplined portfolio risk profile, compared with the
assessment answer key's optimal portfolio for each period.

PM starts from the user's saved positions and converts the portfolio into
common risk measures. These include estimated loss in a fixed adverse market
test, bond interest-rate sensitivity, equity exposure, foreign-exchange and US
dollar exposure, concentration, option premium, and use of risk limits. It
compares both the amount and distribution of that risk with the optimal
portfolio.

### The three components

- **Risk size and intended loss — 40 points:** Is the total adverse-market loss
  close to the optimal portfolio, is that loss distributed similarly across
  instruments, is bond interest-rate risk constructed similarly, and are the
  risk limits respected?
- **Aggregate common factors — 35 points:** Do the portfolio's combined bond,
  equity, currency, and US-dollar exposures resemble the optimal portfolio,
  without excessive directional concentration?
- **Risk adaptation — 25 points:** Is the amount of risk per dollar of the
  portfolio appropriate, and does the user increase, reduce, and reallocate
  risk as the optimal portfolio changes between periods?

Each component is calculated on a $0$ to $100$ scale. PM applies the component
weights to form the period score:

$$
P3_p=0.40\,\mathrm{RiskSize}_p
  +0.35\,\mathrm{CommonFactors}_p
  +0.25\,\mathrm{RiskAdaptation}_p.
$$

The final Pillar 3 score is the simple average of all period scores, so every
period has equal weight. Profit and loss may be shown for context but does not
directly add or remove Pillar 3 points.

Pillar 3 can only be scored when the assessment has a complete optimal
portfolio for comparison. It is a post-session score, not an order-control
check.
`;

const PillarThreeOverviewGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-three-overview-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">100 points</Tag>
      </div>
      <Typography.Title id="pillar-three-overview-title" level={4}>
        Pillar 3 overview
      </Typography.Title>
      <Typography.Text type="secondary">
        How portfolio risk construction becomes a score for each period.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {PILLAR_THREE_OVERVIEW}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarThreeOverviewGuide;
